import { FC, useState } from 'react';

import { Button } from '@dynamika-shared/ui/Button/Button';
import { ChevronIcon } from '@dynamika-shared/ui/icons/Chevron';

export type UploadRecipientsExpectedColumnsProps = {
  downloading: boolean;
  onDownloadTemplate: () => void;
  /**
   * Read once, on mount — the block is unmounted while a file holds the slot,
   * so it picks this up again each time the drop zone comes back.
   */
  defaultOpen?: boolean;
};

/**
 * NOTE: mirrors the FRS column table. A description of the template, never
 * the source of it — the backend authors the file the user downloads.
 */
const TEMPLATE_COLUMNS = [
  { name: 'Email', hint: 'required' },
  { name: 'Scope', hint: 'site codes, ; separated - blank means all sites' },
  { name: 'Notify of Own Actions', hint: 'required, Yes or No' },
  { name: 'Events', hint: 'required, ; separated' },
  { name: 'First Name', hint: 'optional' },
  { name: 'Last Name', hint: 'optional' },
];

/**
 * What the CSV must contain, with the blank template to start from. Collapses
 * so someone who already has their file can fold it out of the way.
 */
export const UploadRecipientsExpectedColumns: FC<
  UploadRecipientsExpectedColumnsProps
> = ({ downloading, onDownloadTemplate, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className='mb-2 rounded-sm bg-body px-2 py-1.5'>
      <button
        type='button'
        data-testid='upload-recipients-columns-toggle'
        className='flex w-full items-center justify-between'
        onClick={() => setOpen(current => !current)}
      >
        <span className='text-1424 font-medium text-black'>
          Expected columns
        </span>

        <ChevronIcon dir={open ? 'up' : 'down'} className='h-2 w-2' />
      </button>

      {open && (
        <>
          <hr className='my-1.5 border-brightGray' />

          <ul className='flex list-disc flex-col gap-1 pl-2 text-1624 marker:text-chineseBlack'>
            {TEMPLATE_COLUMNS.map(({ name, hint }) => (
              <li key={name} className='font-medium text-dimGray'>
                <span className='font-semibold text-chineseBlack'>{name}</span>{' '}
                - {hint}
              </li>
            ))}
          </ul>

          <Button
            data-testid='upload-recipients-template'
            rounded
            className='mt-2 w-full !rounded-sm !border-honoluluBlue !bg-dodgerBlue !px-2 !py-[0.6rem] text-1420 !font-normal !text-white'
            iconType='download'
            iconClassName='mr-0.5 h-2.5 w-2.5'
            disabled={downloading}
            loading={downloading}
            onClick={onDownloadTemplate}
          >
            Download Template
          </Button>
        </>
      )}
    </div>
  );
};
